import React, { useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';
import { CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import type {
  ChecklistItemDetails,
  CodingExtractionItem,
} from '../../../types/codingData.types';

// Checklist Item Component with popover for details
export const ChecklistItem: React.FC<{
  label: string;
  status: 'complete' | 'missing' | 'not-applicable';
  details?: ChecklistItemDetails;
  variant?: 'default' | 'compact';
}> = ({ label, status, details, variant = 'default' }) => {
  const isCompact = variant === 'compact';

  // Filter items based on showOnlySignedItems flag
  const displayItems = useMemo(() => {
    if (!details?.items) return [];

    if (details.showOnlySignedItems) {
      // For "Signed" fields, only show items where is_signed.value is true
      return details.items.filter(
        item => item.validations?.is_signed?.value === true
      );
    }

    // For "Attached" fields, show all items
    return details.items;
  }, [details?.items, details?.showOnlySignedItems]);

  // For "Signed" fields that are missing, show items where is_signed is not true
  const unsignedItems = useMemo(() => {
    if (!details?.items || !details.showOnlySignedItems) return [];
    return details.items.filter(
      item => item.validations?.is_signed?.value !== true
    );
  }, [details?.items, details?.showOnlySignedItems]);

  const getIcon = () => {
    switch (status) {
      case 'complete':
        return <CheckCircle className='h-4 w-4 text-green-600' />;
      case 'missing':
        return <XCircle className='h-4 w-4 text-red-600' />;
      case 'not-applicable':
        return <MinusCircle className='h-4 w-4 text-gray-400' />;
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'complete':
        return 'text-green-700';
      case 'missing':
        return 'text-red-700';
      case 'not-applicable':
        return 'text-gray-500';
    }
  };

  const getBgColor = () => {
    switch (status) {
      case 'complete':
        return 'bg-green-50 border-green-100';
      case 'missing':
        return 'bg-red-50 border-red-100';
      case 'not-applicable':
        return 'bg-white';
    }
  };

  // Determine if we have details to show in popover
  const hasDetails =
    details &&
    (displayItems.length > 0 ||
      unsignedItems.length > 0 ||
      details.notQualifyingReasoning);

  const itemContent = (
    <div
      className={`flex items-center ${isCompact ? 'gap-1 px-1 py-1' : 'gap-2 px-3 py-2'} ${!isCompact && getBgColor()} ${!isCompact && 'border'} rounded-lg ${hasDetails ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
    >
      {getIcon()}
      <span className={`text-xs font-medium ${getTextColor()}`}>{label}</span>
    </div>
  );

  if (!hasDetails) {
    return itemContent;
  }

  // Render a single item card
  const renderItemCard = (
    item: CodingExtractionItem,
    index: number,
    isSignedView: boolean,
    variant: 'success' | 'error'
  ) => {
    const bgClass =
      variant === 'success'
        ? 'bg-green-50 border-green-100'
        : 'bg-red-50 border-red-100';
    const borderClass =
      variant === 'success' ? 'border-green-200' : 'border-red-200';

    return (
      <div key={index} className={`p-3 ${bgClass} border rounded-lg space-y-2`}>
        {/* Summary/Content */}
        <div className='text-sm text-gray-800'>
          {item.face_to_face_visit_summary ||
            item.home_health_order ||
            item.authorized_diagnoses}
        </div>

        {/* Source pages */}
        {item.source && Object.keys(item.source).length > 0 && (
          <div className='text-xs text-gray-500'>
            <span className='font-medium'>Source: </span>
            {Object.entries(item.source).map(([doc, pages], docIndex) => (
              <span key={doc}>
                {docIndex > 0 && ', '}
                {doc} (pages {pages.join(', ')})
              </span>
            ))}
          </div>
        )}

        {/* Reasoning - show is_signed.reasoning for Signed views, otherwise general reasoning */}
        {isSignedView && item.validations?.is_signed?.reasoning ? (
          <div
            className={`text-xs text-gray-600 italic border-t ${borderClass} pt-2 mt-2`}
          >
            <span className='font-medium'>Signature: </span>
            {item.validations.is_signed.reasoning}
          </div>
        ) : (
          item.reasoning && (
            <div
              className={`text-xs text-gray-600 italic border-t ${borderClass} pt-2 mt-2`}
            >
              {item.reasoning}
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>{itemContent}</PopoverTrigger>
      <PopoverContent className='w-96 max-h-80 overflow-y-auto' align='start'>
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            {getIcon()}
            <h4 className='font-semibold text-sm'>{label}</h4>
          </div>

          {/* Show signed/complete items */}
          {status === 'complete' && displayItems.length > 0 && (
            <div className='space-y-3'>
              {displayItems.map((item, index) =>
                renderItemCard(
                  item,
                  index,
                  details.showOnlySignedItems ?? false,
                  'success'
                )
              )}
            </div>
          )}

          {/* Show unsigned items for "Signed" fields when status is missing */}
          {status === 'missing' &&
            details.showOnlySignedItems &&
            unsignedItems.length > 0 && (
              <div className='space-y-3'>
                <div className='text-xs text-red-700 font-medium'>
                  Documents found but not properly signed (see reasoning below):
                </div>
                {unsignedItems.map((item, index) =>
                  renderItemCard(item, index, true, 'error')
                )}
              </div>
            )}

          {/* Show not qualifying reasoning when no items exist */}
          {status === 'missing' &&
            details.notQualifyingReasoning &&
            (!details.showOnlySignedItems ||
              (details.showOnlySignedItems && unsignedItems.length === 0)) && (
              <div className='p-3 bg-red-50 border border-red-100 rounded-lg'>
                <div className='text-sm text-gray-700'>
                  <span className='font-medium text-red-700'>
                    Why this is missing:{' '}
                  </span>
                  {details.notQualifyingReasoning}
                </div>
              </div>
            )}

          {/* Fallback when no details available */}
          {displayItems.length === 0 &&
            unsignedItems.length === 0 &&
            !details.notQualifyingReasoning && (
              <div className='text-sm text-gray-500 italic'>
                No additional details available.
              </div>
            )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
